import { consume } from '@lit/context';
import { LitElement, css, html, nothing, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { styleMap } from 'lit/directives/style-map.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { NOTICE_COLUMN_MAX_PX } from '../../../theme/notice.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/** Legacy's `.hidemsg` fade: 1.5s linear, ending as the notice is removed. */
const FADE_OUT_MS = 1500;

/**
 * Game Notification - Server error and status message overlay
 *
 * Displays alert/information banners for server errors (disconnect, ban, etc.)
 * with optional RECONNECT button. Auto-dismisses after the specified timeout.
 *
 * @element game-notification
 */
@customElement('game-notification')
export class GameNotification extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 50;
        /* Declared here rather than inherited from the body — the component
           owns its own typography, and a slow tap on RECONNECT shouldn't
           start an iOS text selection, callout or grey tap flash. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      /* Pop in, then legacy's 1.5s linear fade out, timed to finish as the
         dismiss timer removes the notice: --hide-delay / --hide-duration are
         set per notice from its timeoutMs (see render). Pure CSS, so there's
         no hiding state to track — the thing that bit kill-notice. */
      .notification {
        position: absolute;
        text-align: center;
        text-shadow: var(--shadow-notice);
        color: var(--color-text-heading);
        font-size: var(--font-size-xxl);
        width: 60%;
        left: 20%;
        animation:
          msgshow 0.4s ease-in-out 1 forwards,
          msghide var(--hide-duration, 0s) linear var(--hide-delay, 0s) 1 forwards;
      }

      /* A notice without a timeout stays until the code that raised it clears it: it pops in and never fades. */
      .notification.held {
        animation: msgshow 0.4s ease-in-out 1 forwards;
      }

      /* pre-line on the message only, so its \n becomes a line break. On the
         whole .notification it also kept the newlines in this template's own
         formatting: a blank line above every notice (~27px lower than legacy)
         and another before RECONNECT. */
      .notification .text {
        white-space: pre-line;
      }

      /* Legacy's 13% / 18%, floored at 70px: on a landscape phone 13% of 390px
         is 51px, inside the 58px-tall top bar. The old blank line had been
         hiding that; without it the floor keeps the notice below the chips.
         Plus the status-bar inset, which moves the top bar and the rows under
         it down in an installed app held upright (see airmash-game). */
      .notification.alert {
        top: calc(max(13%, 70px) + env(safe-area-inset-top, 0px));
      }

      .notification.information {
        top: calc(max(18%, 70px) + env(safe-area-inset-top, 0px));
      }

      @keyframes msghide {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }

      @keyframes msgshow {
        0% {
          opacity: 1;
        }
        1% {
          transform: scale(1);
        }
        30% {
          transform: scale(1.25);
        }
        100% {
          transform: scale(1);
        }
      }

      .info {
        padding: 2px 6px;
        border-radius: var(--radius-md);
        margin-right: 8px;
        background-color: var(--color-bg-chip);
        font-weight: bold;
        font-size: var(--font-size-16);
        vertical-align: 1px;
      }

      .button {
        display: inline-block;
        position: relative;
        vertical-align: top;
        padding: 9px 18px 7px 18px;
        background-color: var(--color-btn-login);
        border-radius: var(--radius-sm);
        font-size: var(--font-size-16);
        font-weight: bold;
        cursor: pointer;
        color: var(--color-text-white);
        text-shadow: var(--shadow-text-btn);
        border-bottom: 2px solid var(--color-border-soft);
        margin-top: 16px;
        pointer-events: auto;
      }

      /* Gate hover under (hover: hover) so iOS sticky-hover doesn't leave the
         RECONNECT / switch-game button stuck in its hover style after a tap —
         these notifications (disconnect, etc.) fire on mobile too. */
      @media (hover: hover) {
        .button:hover {
          background-color: var(--color-btn-login-hover);
          box-shadow: var(--shadow-btn-hover);
        }
      }

      .button:active {
        border-bottom: none;
        margin-top: 18px;
      }

      /* In airmash-game's notice column (.notice-lanes) on a compact viewport:
         the host is a row of the column and the notice a block in it, centred
         by its margins, rather than a band of its own. */
      @media (max-width: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px), (max-height: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px) {
        :host {
          position: relative;
          height: auto;
        }

        .notification,
        .notification.alert,
        .notification.information {
          position: relative;
          top: auto;
          left: auto;
          /* Wider than the desktop's 60%, so the message wraps less and the
             column stays short */
          width: 90%;
          margin: 0 auto 8px;
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  private dismissTimer: number | null = null;
  private lastNoticeId: number = -1;

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this.dismissTimer !== null) {
      window.clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }

  override updated() {
    const notification = this.uiState.gameNotification;

    if (!notification) {
      this.lastNoticeId = -1;
      if (this.dismissTimer !== null) {
        window.clearTimeout(this.dismissTimer);
        this.dismissTimer = null;
      }
      return;
    }

    if (notification.id === this.lastNoticeId) return;
    this.lastNoticeId = notification.id;

    if (this.dismissTimer !== null) {
      window.clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }

    // No timeout: held until the code that raised it clears it
    if (notification.timeoutMs === 0) return;

    const capturedId = notification.id;
    this.dismissTimer = window.setTimeout(() => {
      this.dismissTimer = null;
      const current = this.uiState.gameNotification;
      if (current && current.id === capturedId) {
        this.lastNoticeId = -1;
        this.api.dismissGameNotificationIfMatching(capturedId);
      }
    }, notification.timeoutMs);
  }

  override render() {
    const notification = this.uiState.gameNotification;
    if (!notification) return nothing;
    const switchGameButton = notification.switchGameButton;

    // The fade ends exactly as the dismiss timer fires; a notice shorter than
    // the fade just fades for its whole life. A held notice has no fade at all.
    const held = notification.timeoutMs === 0;
    const hideDuration = Math.min(FADE_OUT_MS, notification.timeoutMs);
    const fadeTiming = held
      ? nothing
      : styleMap({
          '--hide-duration': `${hideDuration}ms`,
          '--hide-delay': `${notification.timeoutMs - hideDuration}ms`,
        });

    // <wbr> after the title badge: the badge's gap is margin, not a space, so
    // without it the badge and the message's first word are one unbreakable
    // run. On a narrow phone "DISCONNECTED Connection" overflowed the 60%-wide
    // box (53px past it at 390px, off-screen at 320px). Now the badge takes
    // its own line when it must, with no width added when it doesn't.
    return keyed(
      notification.id,
      html`
        <div class="notification ${notification.type}${held ? ' held' : ''}" style=${fadeTiming}>
          ${notification.title ? html`<span class="info">${notification.title}</span><wbr />` : nothing}<span
            class="text"
            >${notification.message}</span
          >
          ${
            notification.showReconnect
              ? html`<br /><span class="button" @click=${this.handleReconnect}>RECONNECT</span>`
              : nothing
          }
          ${
            switchGameButton
              ? html`<br /><span class="button" @click=${() => this.handleSwitchGame(switchGameButton.serverId)}
                    >${switchGameButton.label}</span
                  >`
              : nothing
          }
        </div>
      `,
    );
  }

  private handleReconnect = () => {
    this.api.playClick();
    this.api.reconnect();
  };

  private handleSwitchGame = (serverId: string) => {
    this.api.playClick();
    this.api.switchGame(serverId);
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'game-notification': GameNotification;
  }
}
